﻿import React, { useState, useEffect } from 'react';
import { FaFileAlt, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import { DocumentTypeModel, DocumentTypeCategoryModel } from '../../types/document';

const COLOUR_OPTIONS = [
  { value: '',        label: '— None —' },
  { value: 'blue',   label: 'Blue' },
  { value: 'green',  label: 'Green' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'purple', label: 'Purple' },
  { value: 'red',    label: 'Red' },
  { value: 'pink',   label: 'Pink' },
  { value: 'orange', label: 'Orange' },
  { value: 'teal',   label: 'Teal' },
  { value: 'gray',   label: 'Gray' },
];

const COLOUR_CLASSES: Record<string, string> = {
  blue:   'bg-blue-500',
  green:  'bg-green-500',
  yellow: 'bg-yellow-400',
  purple: 'bg-purple-500',
  red:    'bg-red-500',
  pink:   'bg-pink-500',
  orange: 'bg-orange-500',
  teal:   'bg-teal-500',
  gray:   'bg-gray-400',
};

const emptyType = (): Partial<DocumentTypeModel> => ({
  id: undefined,
  name: '',
  description: '',
  colour: '',
  primary_category: null,
  secondary_category: null,
});

const DocumentTypes = () => {
  const { token } = useAuth();
  const [isModalOpen, setIsModalOpen]     = useState(false);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeModel[]>([]);
  const [primaryCats, setPrimaryCats]     = useState<DocumentTypeCategoryModel[]>([]);
  const [secondaryCats, setSecondaryCats] = useState<DocumentTypeCategoryModel[]>([]);
  const [currentType, setCurrentType]     = useState<Partial<DocumentTypeModel>>(emptyType());
  const [searchTerm, setSearchTerm]       = useState('');
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [pageSize, setPageSize]           = useState(10);
  const [currentPage, setCurrentPage]     = useState(1);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    if (!token) return;
    try {
      const [typesRes, primaryRes, secondaryRes] = await Promise.all([
        apiCall('/documents/document-types/', { method: 'GET' }, token),
        apiCall('/documents/document-type-primary-categories/', { method: 'GET' }, token),
        apiCall('/documents/document-type-secondary-categories/', { method: 'GET' }, token),
      ]);
      if (typesRes.ok) {
        const data = await typesRes.json();
        setDocumentTypes(data.results || data || []);
      }
      if (primaryRes.ok) {
        const data = await primaryRes.json();
        setPrimaryCats(data.results || data || []);
      }
      if (secondaryRes.ok) {
        const data = await secondaryRes.json();
        setSecondaryCats(data.results || data || []);
      }
    } catch (error) {
      console.error('Error fetching document types:', error);
    }
  };

  const handleAddNew = () => {
    setCurrentType(emptyType());
    setIsModalOpen(true);
  };

  const handleEdit = (type: DocumentTypeModel) => {
    setCurrentType({ ...type });
    setIsModalOpen(true);
  };

  const handleDelete = async (typeId: string, name: string) => {
    if (!window.confirm(`Delete document type "${name}"?`)) return;
    if (!token) return;
    try {
      const response = await apiCall(`/documents/document-types/${typeId}/`, { method: 'DELETE' }, token);
      if (response.ok) {
        setDocumentTypes(prev => prev.filter(t => t.id !== typeId));
      } else {
        alert('Failed to delete document type');
      }
    } catch {
      alert('Failed to delete document type');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setIsSubmitting(true);
    try {
      const url    = currentType.id ? `/documents/document-types/${currentType.id}/` : '/documents/document-types/';
      const method = currentType.id ? 'PUT' : 'POST';
      const response = await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:               currentType.name,
          description:        currentType.description || null,
          colour:             currentType.colour || null,
          primary_category:   currentType.primary_category || null,
          secondary_category: currentType.secondary_category || null,
        }),
      }, token);
      if (response.ok) {
        await fetchAll();
        setIsModalOpen(false);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to save document type: ${JSON.stringify(err)}`);
      }
    } catch {
      alert('Failed to save document type');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTypes  = documentTypes.filter(t => t.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPages     = Math.ceil(filteredTypes.length / pageSize);
  const paginatedTypes = filteredTypes.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
          <FaFileAlt className="text-primary" />
          Document Types
        </h1>
        <button onClick={handleAddNew} className="btn btn-primary">
          <FaPlus className="w-4 h-4 mr-2" /> Add Document Type
        </button>
      </div>

      {/* Search + Page Size */}
      <div className="mb-6 flex flex-wrap gap-4 justify-between items-center">
        <div className="join">
          <div className="relative join-item">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg className="w-4 h-4 text-base-content/60" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search document types..."
              className="input input-bordered w-64 pl-10"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <button className="btn btn-primary join-item">Search</button>
        </div>
        <select
          className="select select-bordered"
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
        >
          <option value="5">5 per page</option>
          <option value="10">10 per page</option>
          <option value="20">20 per page</option>
          <option value="50">50 per page</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-base-100 rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Primary Category</th>
                <th>Secondary Category</th>
                <th>Colour</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTypes.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-base-content/50 py-8">No document types found</td></tr>
              ) : paginatedTypes.map((type) => (
                <tr key={type.id}>
                  <td className="font-medium">{type.name}</td>
                  <td className="text-sm text-base-content/70 max-w-xs truncate">{type.description || '—'}</td>
                  <td>{type.primary_category_name || '—'}</td>
                  <td>{type.secondary_category_name || '—'}</td>
                  <td>
                    {type.colour ? (
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full ${COLOUR_CLASSES[type.colour] ?? 'bg-base-300'}`} />
                        <span className="text-sm capitalize">{type.colour}</span>
                      </div>
                    ) : '—'}
                  </td>
                  <td>
                    <div className="flex justify-center gap-2">
                      <button onClick={() => handleEdit(type)} className="btn btn-ghost btn-xs" title="Edit">
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(type.id, type.name)} className="btn btn-ghost btn-xs text-error" title="Delete">
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4">
            <button className="btn btn-ghost btn-sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
            {[...Array(totalPages)].map((_, i) => (
              <button key={i + 1} className={`btn btn-sm ${currentPage === i + 1 ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-base-100 p-6 rounded-lg shadow-xl w-full max-w-lg">
            <h2 className="text-xl font-semibold mb-4">
              {currentType.id ? 'Edit Document Type' : 'Add Document Type'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">

                <div>
                  <label className="block mb-1 font-medium">Name <span className="text-error">*</span></label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="e.g. Referral Letter"
                    value={currentType.name ?? ''}
                    onChange={(e) => setCurrentType({ ...currentType, name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium">Description</label>
                  <textarea
                    className="textarea textarea-bordered w-full"
                    placeholder="Brief description of this document type"
                    value={currentType.description ?? ''}
                    onChange={(e) => setCurrentType({ ...currentType, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 font-medium">Primary Category</label>
                    <select
                      className="select select-bordered w-full"
                      value={currentType.primary_category ?? ''}
                      onChange={(e) => setCurrentType({ ...currentType, primary_category: e.target.value || null })}
                    >
                      <option value="">— None —</option>
                      {primaryCats.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Secondary Category</label>
                    <select
                      className="select select-bordered w-full"
                      value={currentType.secondary_category ?? ''}
                      onChange={(e) => setCurrentType({ ...currentType, secondary_category: e.target.value || null })}
                    >
                      <option value="">— None —</option>
                      {secondaryCats.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-medium">Colour</label>
                  <div className="flex items-center gap-3">
                    <select
                      className="select select-bordered flex-1"
                      value={currentType.colour ?? ''}
                      onChange={(e) => setCurrentType({ ...currentType, colour: e.target.value })}
                    >
                      {COLOUR_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {currentType.colour && (
                      <div className={`w-8 h-8 rounded-full flex-shrink-0 ${COLOUR_CLASSES[currentType.colour] ?? 'bg-base-300'}`} />
                    )}
                  </div>
                </div>

              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button type="button" className="btn btn-ghost" onClick={() => setIsModalOpen(false)} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentTypes;
